import { useLayoutEffect, useRef, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { usePathname } from 'next/navigation';
import { isLoggedOutRoute } from '@/utils/match-routes';
import { markBootStalled } from '@/utils/sessionFallback';
import { reportSessionLoss } from '@/utils/sessionTelemetry';

/* MFE boot probes give up late (Auth at 30s, Settings and Garage at 120s), so a boot unresolved
 * at 18s is wedged: with session cookies the host shows its error page and Refresh, else '/'. */
// Session renewal has to settle inside this window. It runs two 6s attempts with a 1s gap, so
// its worst case is 13s, leaving 5s for the bundle load and the probes that run before it. A
// renewal allowed to outlast the backstop would show a member with a live session the retry screen.
const OVERLAY_SAFETY_TIMEOUT_MS = 18000;

interface UseOverlaySafetyTimeoutArgs {
	isLoading: boolean;
	isProcessing: boolean;
	routedMfe: string | null;
	authSettled: boolean;
	setIsProcessing: (value: boolean) => void;
	hasSessionCookies: boolean;
	bootAttempt: number;
	loadingSources: string;
	loadStartedAt: number | null;
}

interface OverlaySafetyTimeout {
	hasTimedOut: boolean;
	awaitingRetry: boolean;
}

/** Releases a stalled overlay; an unsettled boot with session cookies waits for a retry. */
const useOverlaySafetyTimeout = ({
	isLoading,
	isProcessing,
	routedMfe,
	authSettled,
	setIsProcessing,
	hasSessionCookies,
	bootAttempt,
	loadingSources,
	loadStartedAt
}: UseOverlaySafetyTimeoutArgs): OverlaySafetyTimeout => {
	const pathname = usePathname();
	const [hasTimedOut, setHasTimedOut] = useState(false);
	const [awaitingRetry, setAwaitingRetry] = useState(false);
	const deadlineRef = useRef<number | null>(null);
	const armedAttemptRef = useRef(bootAttempt);
	const armedPageRef = useRef(routedMfe);
	const processingStartRef = useRef<number | null>(null);
	const retryStartRef = useRef<number | null>(null);
	const reportedStallRef = useRef(false);
	const reportedDeadlineRef = useRef<number | null>(null);

	// A layout effect, so a bound already spent on return releases before the overlay is painted.
	useLayoutEffect(() => {
		if (!isLoading || !isProcessing) processingStartRef.current = null;
		else processingStartRef.current ??= Date.now();
		const samePage = armedPageRef.current === routedMfe;
		armedPageRef.current = routedMfe;

		if (!isLoading) {
			deadlineRef.current = null;
			retryStartRef.current = null;
			setHasTimedOut(false);
			setAwaitingRetry(false);
			return undefined;
		}

		const isRetry = armedAttemptRef.current !== bootAttempt;
		if (isRetry) {
			armedAttemptRef.current = bootAttempt;
			retryStartRef.current = Date.now();
		}

		// Processing keeps its start across navigations; a load the member left no longer counts.
		const starts = [processingStartRef.current, loadStartedAt].filter((t) => t !== null);
		const startedAt = Math.max(Math.min(...starts, Date.now()), retryStartRef.current ?? 0);
		const sourceDeadline = startedAt + OVERLAY_SAFETY_TIMEOUT_MS;
		// On one page the overlay stayed up throughout, so a source finishing never extends the bound.
		const heldDeadline = samePage && !isRetry ? deadlineRef.current : null;
		const deadline =
			heldDeadline === null ? sourceDeadline : Math.min(heldDeadline, sourceDeadline);
		if (deadline !== deadlineRef.current) {
			deadlineRef.current = deadline;
			setHasTimedOut(false);
			setAwaitingRetry(false);
		} else if (hasTimedOut) {
			return undefined;
		}

		const release = () => {
			setHasTimedOut(true);
			// A later stall is an MFE load, where moving a signed-in user off their route is worse.
			const bootStalled = !authSettled && !isLoggedOutRoute('auth', pathname);

			// Processing stays true, so the stall cannot latch `authSettled` and a retry can re-arm.
			if (bootStalled && hasSessionCookies) {
				if (!reportedStallRef.current) reportSessionLoss('boot_backstop');
				reportedStallRef.current = true;
				setAwaitingRetry(true);
				return;
			}

			setIsProcessing(false);
			// The session is intact here, so this is an error report (once per stuck load), not a logout.
			if (authSettled && Bugsnag.isStarted() && reportedDeadlineRef.current !== deadline) {
				reportedDeadlineRef.current = deadline;
				Bugsnag.notify(
					new Error('Overlay safety timeout released a stuck overlay'),
					(event) => {
						event.addMetadata('overlay', { loadingSources });
					}
				);
			}

			// With no session cookie there was no session to lose, so there is nothing to report.
			if (bootStalled) markBootStalled();
		};

		const remaining = deadline - Date.now();
		if (remaining <= 0) {
			release();
			return undefined;
		}
		const timer = setTimeout(release, remaining);

		return () => clearTimeout(timer);
	}, [
		isLoading,
		isProcessing,
		hasTimedOut,
		authSettled,
		pathname,
		setIsProcessing,
		hasSessionCookies,
		bootAttempt,
		loadingSources,
		loadStartedAt,
		routedMfe
	]);

	return { hasTimedOut, awaitingRetry };
};

export default useOverlaySafetyTimeout;
